import React, { useState, useMemo, useEffect } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Checkbox, Button, Box, Paper, Typography, TableSortLabel
} from "@mui/material";

function compareValues(a, b) {
  const na = Number(a);
  const nb = Number(b);
  if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
}

function formatNumber(value, decimals = 2) {
  const n = Number(value);
  return value == null || value === "" || !Number.isFinite(n)
    ? "-"
    : n.toFixed(decimals);
}

export default function RotationTable({
  rows = [],
  mode = "rotation",
  loading = false,
  onAddToSipp,
  onAddToIsa,
  onAddToTrading,
  onRemoveSelected,
  onChartToggle,
  chartSelectedIds = [],
  clearSelectionSignal,
  isAdmin = false,
  onRemoveFund
}) {
  const [selected, setSelected] = useState([]);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");

  useEffect(() => {
    if (clearSelectionSignal !== undefined) {
      setSelected([]);
    }
  }, [clearSelectionSignal]);

  const visibleIds = rows.map(r => r.fund_id);
  const visibleSelected = selected.filter(id => visibleIds.includes(id));
  const selectedVisibleCount = visibleIds.filter(id => selected.includes(id)).length;
  const allChecked = visibleIds.length > 0 && selectedVisibleCount === visibleIds.length;
  const someChecked = selectedVisibleCount > 0 && selectedVisibleCount < visibleIds.length;

  const sortedRows = useMemo(() => {
    if (!sortColumn) return rows;
    const col = sortColumn;
    return [...rows].sort((a, b) => {
      const av = a[col];
      const bv = b[col];
      const aNull = av == null || av === "";
      const bNull = bv == null || bv === "";
      if (aNull && bNull) return 0;
      if (aNull) return 1;
      if (bNull) return -1;
      const base = compareValues(av, bv);
      return sortDirection === "asc" ? base : -base;
    });
  }, [rows, sortColumn, sortDirection]);

  const toggleSelect = (id) => {
    setSelected(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    setSelected(prev => {
      const cur = rows.map(r => r.fund_id);
      const all = cur.length > 0 && cur.every(id => prev.includes(id));
      if (all) return prev.filter(id => !cur.includes(id));
      return [...new Set([...prev, ...cur])];
    });
  };

  const handleSort = (col) => {
    setSortColumn(col);
    setSortDirection(prev =>
      sortColumn === col ? (prev === "asc" ? "desc" : "asc") : "asc"
    );
  };

  const toggleChartSelect = (id) => {
    const checked = !chartSelectedIds.includes(id);
    onChartToggle?.(id, checked);
  };

  // Loading state first — "No data available." must not flash while fetching.
  if (loading) {
    return (
      <Typography sx={{ p: 2, color: "text.secondary" }}>
        Loading portfolio data...
      </Typography>
    );
  }

  if (!rows || rows.length === 0) {
    return (
      <Typography sx={{ p: 2, color: "text.secondary" }}>
        No data available.
      </Typography>
    );
  }

  const sortProps = (col) => ({
    active: sortColumn === col,
    direction: sortColumn === col ? (sortDirection === "asc" ? "asc" : "desc") : "asc",
    onClick: () => handleSort(col),
  });

  return (
    <Paper sx={{ p: 2, overflowX: "auto" }}>
      <Box sx={{ mb: 2 }}>
        {mode === "rotation" && (
          <>
            <Button variant="contained" sx={{ mr: 2 }} disabled={visibleSelected.length === 0}
              onClick={() => onAddToSipp?.(visibleSelected)}>Add to SIPP</Button>
            <Button variant="contained" sx={{ mr: 2 }} disabled={visibleSelected.length === 0}
              onClick={() => onAddToIsa?.(visibleSelected)}>Add to ISA</Button>
            <Button variant="contained" disabled={visibleSelected.length === 0}
              onClick={() => onAddToTrading?.(visibleSelected)}>Add to Trading</Button>
          </>
        )}
        {mode === "portfolio" && (
          <Button variant="contained" color="error" disabled={visibleSelected.length === 0}
            onClick={() => onRemoveSelected?.(visibleSelected)}>Remove Selected</Button>
        )}
      </Box>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ height: 32 }}>
              <TableCell padding="none" sx={{ width: 32 }}>
                <Checkbox size="small" checked={allChecked} indeterminate={someChecked} onChange={toggleSelectAll} />
              </TableCell>
              <TableCell sx={{ py: 0.5 }}><TableSortLabel {...sortProps("final_rank")}>Rank</TableSortLabel></TableCell>
              <TableCell sx={{ py: 0.5 }}><TableSortLabel {...sortProps("name")}>Name</TableSortLabel></TableCell>
              <TableCell sx={{ py: 0.5 }}><TableSortLabel {...sortProps("price")}>Price</TableSortLabel></TableCell>
              <TableCell sx={{ py: 0.5 }}><TableSortLabel {...sortProps("ma20")}>MA20</TableSortLabel></TableCell>
              <TableCell sx={{ py: 0.5 }}><TableSortLabel {...sortProps("ma50")}>MA50</TableSortLabel></TableCell>
              <TableCell sx={{ py: 0.5 }}><TableSortLabel {...sortProps("r3")}>R3</TableSortLabel></TableCell>
              <TableCell sx={{ py: 0.5 }}><TableSortLabel {...sortProps("r6")}>R6</TableSortLabel></TableCell>
              <TableCell sx={{ py: 0.5 }}><TableSortLabel {...sortProps("r12")}>R12</TableSortLabel></TableCell>
              <TableCell sx={{ py: 0.5 }}><TableSortLabel {...sortProps("final_score")}>Score</TableSortLabel></TableCell>
              <TableCell sx={{ py: 0.5 }}>Decision</TableCell>
              <TableCell padding="none" sx={{ width: 32 }}>Chart</TableCell>
              {isAdmin && onRemoveFund && (
                <TableCell padding="none" sx={{ width: 40 }}>Del</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows.map((r) => {
              const id = r.fund_id;
              return (
                <TableRow key={id} sx={{ height: 32 }}>
                  <TableCell padding="none" sx={{ width: 32 }}>
                    <Checkbox size="small" checked={selected.includes(id)} onChange={() => toggleSelect(id)} />
                  </TableCell>
                  <TableCell sx={{ py: 0.5 }}>{r.final_rank ?? "-"}</TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    <a href={`https://uk.finance.yahoo.com/quote/${encodeURIComponent(r.ticker || "")}`}
                      target="_blank" rel="noopener noreferrer"
                      style={{ textDecoration: "none", color: "#1976d2", fontWeight: 500 }}>
                      {r.name}
                    </a>
                  </TableCell>
                  <TableCell sx={{ py: 0.5 }}>{formatNumber(r.price, 2)}</TableCell>
                  <TableCell sx={{ py: 0.5 }}>{formatNumber(r.ma20, 3)}</TableCell>
                  <TableCell sx={{ py: 0.5 }}>{formatNumber(r.ma50, 3)}</TableCell>
                  <TableCell sx={{ py: 0.5 }}>{formatNumber(r.r3, 2)}</TableCell>
                  <TableCell sx={{ py: 0.5 }}>{formatNumber(r.r6, 2)}</TableCell>
                  <TableCell sx={{ py: 0.5 }}>{formatNumber(r.r12, 2)}</TableCell>
                  <TableCell sx={{ py: 0.5 }}>{formatNumber(r.final_score, 2)}</TableCell>
                  <TableCell sx={{ py: 0.5, fontWeight: "bold",
                    color: r.final_decision === "BUY" ? "green" : r.final_decision === "SELL" ? "red" : "orange" }}>
                    {r.final_decision}
                  </TableCell>
                  <TableCell padding="none" sx={{ width: 32 }}>
                    <Checkbox size="small"
                      checked={chartSelectedIds.includes(id)}
                      onChange={() => toggleChartSelect(id)} />
                  </TableCell>
                  {isAdmin && onRemoveFund && (
                    <TableCell padding="none" sx={{ width: 40 }}>
                      <Button size="small" color="error" sx={{ minWidth: 0, p: 0.5 }}
                        onClick={() => onRemoveFund(id)}
                        aria-label={`Remove ${r.name || r.ticker}`}>X</Button>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
